// Expenses.js - Complete Unified Component
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaMoneyBillWave,
  FaPlusCircle,
  FaFilter,
  FaCalendarAlt,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
  FaRegComment,
  FaTimes,
  FaFileInvoiceDollar,
  FaListUl
} from "react-icons/fa";
import { FaShoppingCart } from "react-icons/fa";
import { MdOutlineArrowDropDown, MdOutlineFileDownload } from "react-icons/md";
import './Expenses.css';

const Expenses = () => {
  const navigate = useNavigate();

  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showPayBillsDropdown, setShowPayBillsDropdown] = useState(false);
  const [showNewTransactionDropdown, setShowNewTransactionDropdown] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Filter modal states
  const [filterDates, setFilterDates] = useState('all');
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [filterExpenseDate, setFilterExpenseDate] = useState('all');

  // Settings options state
  const [settings, setSettings] = useState({
    showAllTransactions: true,
    showDate: true,
    showType: true,
    showNumber: true,
    showPayee: true,
    showClass: false,
    showLocation: false,
    showStatus: false
  });

  // ✅ No hardcoded data - load real data from backend here (setExpensesData)
  const [expensesData] = useState([]);

  // Supplier options come from the data itself (payee names)
  const supplierOptions = [...new Set(expensesData.map(item => item.payee).filter(Boolean))];

  // Transaction types for New Transaction dropdown
  const transactionTypes = [
    { name: 'Expense', route: '/expense/new' },
    { name: 'Bill', route: '/bill/new' },
    { name: 'Cheque', route: '/cheque/new' },
    { name: 'Purchase Order', route: '/purchase-order/new' },
    { name: 'Supplier Credit', route: '/supplier-credit/new' },
    { name: 'Credit Card Credit', route: '/credit-card-credit/new' },
    { name: 'Pay Down Credit Card', route: '/pay-down-credit-card/new' },
    { name: 'Import Bills', route: '/import-bills/new' },
    { name: 'Supplier refund', route: '/supplier-refund/new' }
  ];

  // Refs for dropdowns
  const payBillsRef = useRef(null);
  const newTransactionRef = useRef(null);
  const payBillsBtnRef = useRef(null);
  const newTransactionBtnRef = useRef(null);
  const feedbackBtnRef = useRef(null);
  const filterBtnRef = useRef(null);
  const filterDropdownRef = useRef(null);
  const filterModalRef = useRef(null);
  const dateBtnRef = useRef(null);
  const dateDropdownRef = useRef(null);
  const settingsBtnRef = useRef(null);
  const settingsDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (payBillsBtnRef.current && !payBillsBtnRef.current.contains(event.target) &&
          payBillsRef.current && !payBillsRef.current.contains(event.target)) {
        setShowPayBillsDropdown(false);
      }
      if (newTransactionBtnRef.current && !newTransactionBtnRef.current.contains(event.target) &&
          newTransactionRef.current && !newTransactionRef.current.contains(event.target)) {
        setShowNewTransactionDropdown(false);
      }
      if (filterBtnRef.current && !filterBtnRef.current.contains(event.target) &&
          filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
      if (dateBtnRef.current && !dateBtnRef.current.contains(event.target) &&
          dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setShowDateDropdown(false);
      }
      if (settingsBtnRef.current && !settingsBtnRef.current.contains(event.target) &&
          settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target)) {
        setShowSettingsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const handleSettingChange = (settingName) => {
    setSettings(prev => ({
      ...prev,
      [settingName]: !prev[settingName]
    }));
  };

  const settingsOptions = [
    { key: 'showDate', label: 'Show Date' },
    { key: 'showType', label: 'Show Type' },
    { key: 'showNumber', label: 'Show Number' },
    { key: 'showPayee', label: 'Show Payee' },
    { key: 'showClass', label: 'Show Class' },
    { key: 'showLocation', label: 'Show Location' },
    { key: 'showStatus', label: 'Show Status' }
  ];

  const handleFeedback = () => {
    navigate('/feedback');
  };

  const handlePayBills = () => {
    setShowPayBillsDropdown(!showPayBillsDropdown);
    setShowNewTransactionDropdown(false);
  };

  const handleNewTransaction = () => {
    setShowNewTransactionDropdown(!showNewTransactionDropdown);
    setShowPayBillsDropdown(false);
  };

  const handleMakeRecurringPayment = () => {
    alert('Make Recurring Payment - This will open payment setup form');
    setShowPayBillsDropdown(false);
  };

  const handleTransactionType = (type, route) => {
    setShowNewTransactionDropdown(false);
    if (route) {
      navigate(route);
    } else {
      alert(`Create new ${type}`);
    }
  };

  const handleApplyFilter = () => {
    setShowFilterModal(false);
    alert(`Filter applied - Dates: ${filterDates}, Supplier: ${filterSupplier}, Expense Date: ${filterExpenseDate}`);
  };

  const handleResetFilter = () => {
    setFilterDates('all');
    setFilterSupplier('all');
    setFilterExpenseDate('all');
    setSelectedFilter('all');
    setSelectedDateRange('all');
    alert('Filters reset!');
  };

  const getFilteredData = () => {
    let filtered = [...expensesData];

    if (selectedFilter !== 'all') {
      filtered = filtered.filter(item =>
        item.type.toLowerCase() === selectedFilter.toLowerCase()
      );
    }

    if (selectedDateRange !== 'all') {
      const today = new Date();
      if (selectedDateRange === 'today') {
        filtered = filtered.filter(item => item.date === today.toISOString().split('T')[0]);
      } else if (selectedDateRange === 'week') {
        const weekAgo = new Date(today.setDate(today.getDate() - 7));
        filtered = filtered.filter(item => new Date(item.date) >= weekAgo);
      } else if (selectedDateRange === 'month') {
        const monthAgo = new Date(today.setMonth(today.getMonth() - 1));
        filtered = filtered.filter(item => new Date(item.date) >= monthAgo);
      }
    }

    return filtered;
  };

  const currentData = getFilteredData();

  const renderExpensesTable = () => (
    <table className="expenses-table">
      <thead>
        <tr>
          {settings.showDate && <th>Date</th>}
          {settings.showType && <th>Type</th>}
          {settings.showNumber && <th>Number</th>}
          {settings.showPayee && <th>Payee</th>}
          <th>Category</th>
          {settings.showClass && <th>Class</th>}
          {settings.showLocation && <th>Location</th>}
          {settings.showStatus && <th>Status</th>}
          <th>Total</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {currentData.map((item) => (
          <tr key={item.id}>
            {settings.showDate && <td>{item.date}</td>}
            {settings.showType && (
              <td>
                <span className={`transaction-type ${item.type?.toLowerCase().replace(/\s/g, '-')}`}>
                  {item.type}
                </span>
              </td>
            )}
            {settings.showNumber && <td>{item.number}</td>}
            {settings.showPayee && <td>{item.payee}</td>}
            <td>{item.category}</td>
            {settings.showClass && <td>{item.class}</td>}
            {settings.showLocation && <td>{item.location}</td>}
            {settings.showStatus && (
              <td>
                <span className={`status-badge ${item.status?.toLowerCase()}`}>
                  {item.status}
                </span>
              </td>
            )}
            <td className={item.total?.startsWith('-') ? 'negative' : 'positive'}>
              {item.total}
            </td>
            <td>
              <button className="view-btn">
                <FaEye className="view-icon" /> View
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="expenses-main">
      {/* Header: title + badge (left) | icons + action buttons (right) */}
      <div className="expenses-header-section">
        <div className="expenses-header-left">
          <h2>
            <FaShoppingCart className="expenses-title-icon" /> Expenses
          </h2>
        </div>

        <div className="expenses-header-right">
          {/* Export / Print / Settings */}
          <div className="region-tabs-right">
            <button className="icon-btn" title="Export" onClick={() => alert('Export functionality coming soon!')}>
              <MdOutlineFileDownload size="1.2em" />
            </button>

            <button className="icon-btn" title="Print" onClick={handlePrint}>
              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                <path d="M448 192V77.25c0-8.49-3.37-16.62-9.37-22.63L393.37 9.37c-6-6-14.14-9.37-22.63-9.37H96C78.33 0 64 14.33 64 32v160c-35.35 0-64 28.65-64 64v112c0 8.84 7.16 16 16 16h48v96c0 17.67 14.33 32 32 32h320c17.67 0 32-14.33 32-32v-96h48c8.84 0 16-7.16 16-16V256c0-35.35-28.65-64-64-64zm-64 256H128v-96h256v96zm0-224H128V64h192v48c0 8.84 7.16 16 16 16h48v96zm48 72c-13.25 0-24-10.75-24-24 0-13.26 10.75-24 24-24s24 10.74 24 24c0 13.25-10.75 24-24 24z"/>
              </svg>
            </button>
            <div className="settings-wrapper">
              <button
                ref={settingsBtnRef}
                className="icon-btn settings-btn"
                title="Settings"
                onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
              >
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                  <path d="M487.4 315.7l-42.6-24.6c4.3-23.2 4.3-47 0-70.2l42.6-24.6c4.9-2.8 7.1-8.6 5.5-14-11.1-35.6-30-67.8-54.7-94.6-3.8-4.1-10-5.1-14.8-2.3L380.8 110c-17.9-15.4-38.5-27.3-60.8-35.1V25.8c0-5.6-3.9-10.5-9.4-11.7-36.7-8.2-74.3-7.8-109.2 0-5.5 1.2-9.4 6.1-9.4 11.7V75c-22.2 7.9-42.8 19.8-60.8 35.1L88.7 85.5c-4.9-2.8-11-1.9-14.8 2.3-24.7 26.7-43.6 58.9-54.7 94.6-1.7 5.4.6 11.2 5.5 14L67.3 221c-4.3 23.2-4.3 47 0 70.2l-42.6 24.6c-4.9 2.8-7.1 8.6-5.5 14 11.1 35.6 30 67.8 54.7 94.6 3.8 4.1 10 5.1 14.8 2.3l42.6-24.6c17.9 15.4 38.5 27.3 60.8 35.1v49.2c0 5.6 3.9 10.5 9.4 11.7 36.7 8.2 74.3 7.8 109.2 0 5.5-1.2 9.4-6.1 9.4-11.7v-49.2c22.2-7.9 42.8-19.8 60.8-35.1l42.6 24.6c4.9 2.8 11 1.9 14.8-2.3 24.7-26.7 43.6-58.9 54.7-94.6 1.5-5.5-.7-11.3-5.6-14.1zM256 336c-44.1 0-80-35.9-80-80s35.9-80 80-80 80 35.9 80 80-35.9 80-80 80z"/>
                </svg>
                <MdOutlineArrowDropDown className="settings-dropdown-icon" />
              </button>
              {showSettingsDropdown && (
                <div className="settings-dropdown" ref={settingsDropdownRef}>
                  <div className="settings-dropdown-list">
                    {settingsOptions.map((option) => (
                      <label key={option.key} className="settings-option">
                        <input
                          type="checkbox"
                          checked={settings[option.key]}
                          onChange={() => handleSettingChange(option.key)}
                          className="settings-checkbox"
                        />
                        <span className="settings-label">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="expenses-action-buttons">
            <div className="button-wrapper">
              <button
                ref={feedbackBtnRef}
                className="action-btn feedback-btn"
                onClick={handleFeedback}
              >
                <FaRegComment className="btn-icon" />
                Feedback
              </button>
            </div>

            <div className="button-wrapper">
              <button
                ref={payBillsBtnRef}
                className="action-btn paybills-btn"
                onClick={handlePayBills}
              >
                <FaMoneyBillWave className="btn-icon" />
                Pay bills
              </button>
              {showPayBillsDropdown && (
                <div className="action-dropdown paybills-dropdown" ref={payBillsRef}>
                  <div className="dropdown-header">
                    <span>Pay Bills</span>
                  </div>
                  <div className="dropdown-item-action" onClick={handleMakeRecurringPayment}>
                    <div className="dropdown-item-content">
                      <div className="dropdown-item-title">Make Recurring Payment</div>
                      <div className="dropdown-item-desc">Set up automated bill payments</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="button-wrapper">
              <button
                ref={newTransactionBtnRef}
                className="action-btn newtransaction-btn"
                onClick={handleNewTransaction}
              >
                <FaPlusCircle className="btn-icon" />
                New Transaction
              </button>
              {showNewTransactionDropdown && (
                <div className="action-dropdown transaction-dropdown" ref={newTransactionRef}>
                  <div className="dropdown-header">
                    <span>New Transaction</span>
                  </div>
                  <div className="transaction-list">
                    {transactionTypes.map((type, index) => (
                      <div
                        key={index}
                        className="transaction-list-item"
                        onClick={() => handleTransactionType(type.name, type.route)}
                      >
                        <div className="transaction-list-name">{type.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters row */}
      <div className="expenses-filters">
        <div className="filter-left">
          <div className="filter-wrapper">
            <div
              className="filter-dropdown"
              ref={filterBtnRef}
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            >
              <span>All transactions <MdOutlineArrowDropDown className="dropdown-icon" /></span>
            </div>
            {showFilterDropdown && (
              <div className="dropdown-menu filter-type-dropdown" ref={filterDropdownRef}>
                <div className="dropdown-item" onClick={() => {
                  setSelectedFilter('all');
                  setShowFilterDropdown(false);
                }}>All Transactions</div>
                <div className="dropdown-item" onClick={() => {
                  setSelectedFilter('expense');
                  setShowFilterDropdown(false);
                }}>Expenses</div>
                <div className="dropdown-item" onClick={() => {
                  setSelectedFilter('bill');
                  setShowFilterDropdown(false);
                }}>Bills</div>
                <div className="dropdown-item" onClick={() => {
                  setSelectedFilter('purchase');
                  setShowFilterDropdown(false);
                }}>Purchase Orders</div>
              </div>
            )}
          </div>

          <div className="filter-dropdown" onClick={() => setShowFilterModal(true)}>
            <FaFilter className="filter-icon" />
            <span>Filter</span>
          </div>

          <div className="filter-wrapper">
            <div
              className="date-filter"
              ref={dateBtnRef}
              onClick={() => setShowDateDropdown(!showDateDropdown)}
            >
              <FaCalendarAlt className="calendar-icon" />
              <span>Dates: {selectedDateRange === 'all' ? 'All Dates' :
                       selectedDateRange === 'today' ? 'Today' :
                       selectedDateRange === 'week' ? 'Last 7 Days' : 'Last 30 Days'}
                <MdOutlineArrowDropDown className="dropdown-icon" />
              </span>
            </div>
            {showDateDropdown && (
              <div className="dropdown-menu date-dropdown" ref={dateDropdownRef}>
                <div className="dropdown-item" onClick={() => {
                  setSelectedDateRange('all');
                  setShowDateDropdown(false);
                }}>All Dates</div>
                <div className="dropdown-item" onClick={() => {
                  setSelectedDateRange('today');
                  setShowDateDropdown(false);
                }}>Today</div>
                <div className="dropdown-item" onClick={() => {
                  setSelectedDateRange('week');
                  setShowDateDropdown(false);
                }}>Last 7 Days</div>
                <div className="dropdown-item" onClick={() => {
                  setSelectedDateRange('month');
                  setShowDateDropdown(false);
                }}>Last 30 Days</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="filter-modal-overlay">
          <div className="filter-modal" ref={filterModalRef}>
            <div className="filter-modal-header">
              <h3>Filter</h3>
              <button className="close-modal" onClick={() => setShowFilterModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="filter-modal-body">
              <div className="filter-section">
                <label className="filter-label">Dates: All Dates</label>
                <select
                  className="filter-select"
                  value={filterDates}
                  onChange={(e) => setFilterDates(e.target.value)}
                >
                  <option value="all">All Dates</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="last7">Last 7 days</option>
                  <option value="last30">Last 30 days</option>
                  <option value="thisMonth">This month</option>
                  <option value="lastMonth">Last month</option>
                </select>
              </div>

              <div className="filter-section">
                <label className="filter-label">Supplier</label>
                <select
                  className="filter-select"
                  value={filterSupplier}
                  onChange={(e) => setFilterSupplier(e.target.value)}
                >
                  <option value="all">All</option>
                  {supplierOptions.map((supplier) => (
                    <option key={supplier} value={supplier}>{supplier}</option>
                  ))}
                </select>
              </div>

              <div className="filter-section">
                <label className="filter-label">Expense date</label>
                <select
                  className="filter-select"
                  value={filterExpenseDate}
                  onChange={(e) => setFilterExpenseDate(e.target.value)}
                >
                  <option value="all">All Dates</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="last7">Last 7 days</option>
                  <option value="last30">Last 30 days</option>
                  <option value="thisMonth">This month</option>
                </select>
              </div>
            </div>
            <div className="filter-modal-footer">
              <button className="reset-filter-btn" onClick={handleResetFilter}>
                Reset
              </button>
              <button className="apply-filter-btn" onClick={handleApplyFilter}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="expenses-table-container">
        {renderExpensesTable()}

        {currentData.length === 0 && (
          <div className="expenses-empty">
            <FaFileInvoiceDollar className="expenses-empty-icon" />
            <p>No expenses found</p>
          </div>
        )}
      </div>

      <div className="expenses-pagination">
        <button
          className="pagination-btn"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
        >
          <FaChevronLeft /> Previous
        </button>
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(prev => prev + 1)}
        >
          Next <FaChevronRight />
        </button>
      </div>
    </div>
  );
};

export default Expenses;